<template>
  <div class="video">
    <div class="video-container" style="position: relative; width: 100%;">
      <video ref="video"
             id="webcam"
             class="media-video"
             :class="{'environment': facingModeEnvironment}"
             autoplay playsinline muted
             v-show="!isFaceDetected">
      </video>
      <div class="preview" :class="{'on': isFaceDetected}" v-show="isFaceDetected">
        <img ref="outputImg" class="img-preview img-fluid">
      </div>
      <div class="imageView d-none">
        <img ref="outputImgOriginal" id="outputImgOriginal">
      </div>
      <div class="overlay-alert" v-if="showInvalidPhotoMessage || invalidFaceInfoText">
        <div class="video-info video-info-centered">
          <div>
            <Icon name="grFaceFrown" width="36" class="mb-2"/>
            <h3 class="modal-title mb-2">{{ $t('modal_pre_checkin_face_recognition.photo_step.no_face') }}</h3>
            <h4 class="modal-subtitle mb-4">
              <template v-if="invalidFaceInfoText">{{ invalidFaceInfoText }}</template>
              <template v-else>{{ $t('modal_pre_checkin_face_recognition.photo_step.no_face_text') }}</template>
            </h4>
            <b-button variant="primary" block @click="dismissError">
              {{ $t('modal_pre_checkin_face_recognition.photo_step.retake_photo') }}
            </b-button>
          </div>
        </div>
      </div>
      <div class="overlay-alert" v-if="(detecting && !isFaceDetected) || !stream">
        <div class="d-flex align-items-center justify-content-center h-100">
          <div>

            <div class="loader">
              <div class="loader__item"></div>
              <div class="loader__item"></div>
              <div class="loader__item"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="flash" v-show="showFlash"></div>
    </div>
  </div>
</template>

<script>
import {FaceDetector, FilesetResolver} from "@mediapipe/tasks-vision";
//import cameraShutter from '../assets/audio/iphone-camera-capture.mp3'

export default {
  name: 'WebcamStreamFaceDetectorMediaPipe',
  props: {
    defaultCamera: {type: String, required: false, default: 'user'},
    takingSelfie: {type: Boolean, required: false, default: false},
  },

  data() {
    return {
      debug: false,
      constraints: {
        audio: false, // we don't require to have the audio
        video: {

          width: {min: 540, ideal: window.innerHeight > window.innerWidth ? 1920 : 1000},
          height: {min: 400, ideal: window.innerHeight > window.innerWidth ? 1500 : 740},
          // aspectRatio: {ideal: 4/3},
          frameRate: {
            min: 15,
            ideal: 30,
          },
          facingMode: this.defaultCamera
        }
      },

      isFaceDetected: false,
      invalidFaceInfoText: null,
      showInvalidPhotoMessage: false,
      enableDrawDetection: false,
      showFlash: false,
      cameraAudio: null,

      // NEW
      detecting: false,
      faceDetector: FaceDetector,
      runningMode: 'IMAGE',
      video: {},
      liveView: {},
      lastVideoTime: -1,
      children: [],
      detections: [],
      canvasDetection: null,
      enableCapture: false,
      stream: null,
      shotTaken: null

    };
  },
  computed: {
    facingModeEnvironment() {
      return this.constraints.video.facingMode === 'environment'
    },
    hasGetUserMedia() {
      return !!navigator.mediaDevices.getUserMedia;
    },
    mainDetection() {
      return this.detections[0]
    },
    hardwareAccelerationSupported() {
      return this.detectWebGLContext()
    }
  },
  watch: {
    takingSelfie: function (newVal, oldVal) {
      newVal ? this.run() : null
    },
  },
  mounted() {
    this.init()
  },

  methods: {
    async init() {
      this.video = this.$refs.video
      this.liveView = this.$refs.liveView
      this.canvasDetection = this.$refs.canvasDetection
      this.shotTaken = this.$refs.outputImgOriginal

      //this.cameraAudio = new Audio(cameraShutter)

      await this.enableCam()
      if (this.hardwareAccelerationSupported)
        await this.initializeFaceDetector()

      if (!this.hasGetUserMedia) {
        console.warn("getUserMedia() is not supported by your browser");
      }
    },

    async initializeFaceDetector() {
      const vision = await FilesetResolver.forVisionTasks(
          `${process.env.BASE_URL}mediapipe/tasks-vision@0.10.11/wasm`
          //"https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.4/wasm"
      );
      this.faceDetector = await FaceDetector.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: `${process.env.BASE_URL}mediapipe/face_detector/blaze_face_short_range.tflite`, //`https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite`,
          delegate: 'GPU',
        },
        minDetectionConfidence: 0.7,
        runningMode: this.runningMode
      });
    },

    async enableCam() {
      if (!this.faceDetector) {
        alert("Face Detector is still loading. Please try again...")
        return
      }
      // Activate the webcam stream.
      let self = this
      navigator.mediaDevices
          .getUserMedia(this.constraints)
          .then((stream) => {
            this.stream = stream
            self.video.srcObject = stream
            this.enableCapture = true
            setTimeout(() => {
              this.$emit('streamAllowed')
            }, 500)
          })
          .catch(this.handleMediaStreamError)
    },

    async run() {
      if(this.constraints.video.facingMode === 'user')
        this.flash(1000)

      this.detecting = true

      await this.take()
      await this.capture()

    },
    take() {
      return new Promise((resolve, reject) => {
        if (this.debug) this.clear()

        //this.cameraAudio.play()

        let canvas = document.createElement('CANVAS')
        canvas.width = this.video.videoWidth;
        canvas.height = this.video.videoHeight;

        canvas.getContext('2d').drawImage(this.video, 0, 0, this.video.videoWidth, this.video.videoHeight);
        // Image that will be used for detection
        this.shotTaken.src = canvas.toDataURL('image/jpeg')
        this.shotTaken.onload = () => {
          resolve()
        }

      })
    },

    async capture() {

      if (!this.faceDetector) {
        console.log("Wait for objectDetector to load before clicking");
        return;
      }

      // No hw acceleration available we send the whole image
      if (!this.hardwareAccelerationSupported) {
        this.detecting = false
        this.isFaceDetected = true
        this.createImageToSend()
        return
      }

      // faceDetector.detect returns a promise which, when resolved, is an array of Detection faces
      try {
        this.detections = this.faceDetector.detect(this.shotTaken).detections;
        this.detecting = false
        this.isFaceDetected = this.detections && this.detections.length > 0

        if (this.isFaceDetected) {
          // sort detections by area descending
          this.detections = this.detections.sort((a, b) => {
            return (b.boundingBox.width * b.boundingBox.height) - (a.boundingBox.width * a.boundingBox.height)
          });
          if (this.debug) {
            this.displayImageDetections(this.detections, this.shotTaken);
          }
          this.createImageToSend()

        } else {
          this.detecting = false
          this.showInvalidPhotoMessage = true
          this.retakePhoto(true)
        }
      } catch (error) {
        this.detecting = false
        this.isFaceDetected = false
        this.showInvalidPhotoMessage = true
        this.retakePhoto(true)
        console.log(error)
      }

    },

    displayImageDetections(detections, resultElement) {
      this.clear()
      const ratio = resultElement.height / resultElement.naturalHeight;


      for (let detection of detections) {
        // Description text
        const p = document.createElement("p");
        p.setAttribute("class", "info");
        p.innerText =
            "Confidence: " +
            Math.round(parseFloat(detection.categories[0].score) * 100) +
            "% .";
        // Positioned at the top left of the bounding box.
        // Height is whatever the text takes up.
        // Width subtracts text padding in CSS so fits perfectly.
        p.style =
            "left: " +
            detection.boundingBox.originX * ratio +
            "px;" +
            "top: " +
            (detection.boundingBox.originY * ratio - 30) +
            "px; " +
            "width: " +
            (detection.boundingBox.width * ratio - 10) +
            "px;" +
            "hight: " +
            20 +
            "px;";
        const highlighter = document.createElement("div");
        highlighter.setAttribute("class", "highlighter");
        highlighter.style =
            "left: " +
            detection.boundingBox.originX * ratio +
            "px;" +
            "top: " +
            detection.boundingBox.originY * ratio +
            "px;" +
            "width: " +
            detection.boundingBox.width * ratio +
            "px;" +
            "height: " +
            detection.boundingBox.height * ratio +
            "px;";

        resultElement.parentNode.appendChild(highlighter);
        resultElement.parentNode.appendChild(p);
        for (let keypoint of detection.keypoints) {
          const keypointEl = document.createElement("spam");
          keypointEl.className = "key-point";
          keypointEl.style.top = `${keypoint.y * resultElement.height - 3}px`;
          keypointEl.style.left = `${keypoint.x * resultElement.width - 3}px`;
          resultElement.parentNode.appendChild(keypointEl);
        }
      }
      this.createImageToSend()

    },
    createImageToSend() {
      let canvas = document.createElement('CANVAS')

      // 43 % of width
      if (this.hardwareAccelerationSupported) {
        const wPerc = 40
        const hPerc = 60
        const wPadding = this.mainDetection.boundingBox.width * wPerc / 100
        const hPadding = this.mainDetection.boundingBox.width * hPerc / 100
        canvas.width = this.mainDetection.boundingBox.width + wPadding
        canvas.height = this.mainDetection.boundingBox.height + hPadding
        canvas.getContext('2d').drawImage(this.shotTaken,
            this.mainDetection.boundingBox.originX - (wPadding / 2),
            this.mainDetection.boundingBox.originY - (hPadding - (wPadding / 4)),
            this.mainDetection.boundingBox.width + wPadding,
            this.mainDetection.boundingBox.height + hPadding,
            0,
            0,
            this.mainDetection.boundingBox.width + wPadding,
            this.mainDetection.boundingBox.height + hPadding,
        );
      } else {
        canvas.width = this.shotTaken.width;
        canvas.height = this.shotTaken.height
        canvas.getContext('2d').drawImage(this.shotTaken,
            0,
            0,
            this.shotTaken.width,
            this.shotTaken.height
        );
      }

      this.$refs.outputImg.src = canvas.toDataURL('image/jpeg')
      console.log("IMAGE TO SEND")
      canvas.toBlob(blob => {
        this.$emit('photoTaken', {faceImg: blob, faceEncoding: []})
      }, 'image/jpeg');


    },
    clear() {
      const highlighters = this.shotTaken.parentNode.getElementsByClassName(
          "highlighter"
      );
      while (highlighters[0]) {
        highlighters[0].parentNode.removeChild(highlighters[0]);
      }

      const infos = this.shotTaken.parentNode.getElementsByClassName("info");
      while (infos[0]) {
        infos[0].parentNode.removeChild(infos[0]);
      }
      const keyPoints = this.shotTaken.parentNode.getElementsByClassName("key-point");
      while (keyPoints[0]) {
        keyPoints[0].parentNode.removeChild(keyPoints[0]);
      }

    },
    stopMediaStreamTrack() {
      this.video.srcObject = null;
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
      }
    },
    switchCamera() {
      this.constraints.video.facingMode = this.constraints.video.facingMode === 'user' ? 'environment' : 'user'
      this.stopMediaStreamTrack()
      this.init()
    },
    flash(timeout) {
      this.showFlash = true
      setTimeout(() => {
        this.showFlash = false

      }, timeout)


    },

    handleMediaStreamError(error) {
      console.error(error)
     /* let msg = ""
      switch (error.name) {
        case 'NotAllowedError':
          msg = "It's not possible to access your camera at this time. Please make sure you have assigned permissions to the browser."
          break
        case 'NotFoundError':
          msg = "Seems like your device doesn't a camera."
          break
        default:
          msg = "We are sorry, but it's not possible to access your camera at this time."
          break
      }
      alert(msg);*/
      this.$emit('streamNotAllowed', {error: error.name})
    },


    pause() {
      this.video.pause()
    },
    dismissError() {
      this.showInvalidPhotoMessage = false
      this.retakePhoto()
    },
    retakePhoto(hasError) {
      this.$emit('photoRetake', {error: hasError})
    },

    setInvalidState(infoText) {
      // server side invalidation
      this.invalidFaceInfoText = infoText
    },

    resumeVideo() {
      this.video.play()
      this.isFaceDetected = false
      this.invalidFaceInfoText = null
    },
    detectWebGLContext() {
      // Create canvas element. The canvas is not added to the
      // document itself, so it is never displayed in the
      // browser window.
      const canvas = document.createElement("canvas")
      // Get WebGLRenderingContext from canvas element.
      const gl = canvas.getContext("webgl")
          || canvas.getContext("experimental-webgl")
      // Report the result.
      return !!(gl && gl instanceof WebGLRenderingContext)
    }
  },
  beforeDestroy() {
    this.pause()
    this.stopMediaStreamTrack();
  }
}
</script>

<style>
.img-preview {
  transform: rotateY(180deg);
}


.imageView {
  position: relative;
  transform: scaleX(-1);
}


.highlighter {
  background: rgba(0, 255, 0, 0.25);
  border: 1px dashed #fff;
  z-index: 1;
  position: absolute;
}


.key-point {
  position: absolute;
  z-index: 1;
  width: 3px;
  height: 3px;
  background-color: #ff0000;
  /* border: 1px solid #ffffff; */
  border-radius: 50%;
  display: block;
}
</style>
